import React, { useEffect } from "react";
import { Link } from "react-router-dom";

// ——— Custom Styles ———————————————————
const CustomStyles = () => (
  <style>{`
    body { 
      margin: 0; 
      -webkit-font-smoothing: antialiased; 
      -moz-osx-font-smoothing: grayscale; 
      background-color: #0A0A0B;
    }
    .liquid-bg {
      position: fixed;
      top: 0; left: 0; right: 0; bottom: 0;
      background: radial-gradient(circle at 20% 30%, rgba(249, 115, 22, 0.1) 0%, transparent 40%),
                  radial-gradient(circle at 80% 80%, rgba(255, 255, 255, 0.08) 0%, transparent 40%);
      z-index: 0;
      pointer-events: none;
    }
  `}</style>
);

// ——— Helper Components ———————————————————————————————
const Card = ({ title, children, className = "" }) => (
  <div className={`bg-white/[0.02] backdrop-blur-2xl rounded-3xl p-8 border border-white/5 shadow-[0_8px_32px_0_rgba(0,0,0,0.3)] transition-all duration-500 hover:bg-white/[0.03] hover:border-white/10 ${className}`}>
    {title && <h3 className="text-2xl font-bold bg-gradient-to-r from-white to-white/70 bg-clip-text text-transparent mb-6 tracking-tight">{title}</h3>}
    <div className="text-white/70 leading-relaxed space-y-4">
      {children}
    </div>
  </div>
);

const SectionHeading = ({ number, title }) => (
  <div className="flex items-center gap-4 mb-8">
    <div className="flex items-center justify-center w-12 h-12 rounded-full border border-orange-500/30 bg-orange-500/10 text-orange-300 font-bold text-lg font-mono">
      {number}
    </div>
    <h2 className="text-3xl md:text-4xl font-black tracking-tight text-white">{title}</h2>
  </div>
);

export default function TVSCampaignOverview() {
  useEffect(() => {
    document.title = "TVS Launch – Campaign Executive Summary";
  }, []);

  return (
    <div className="min-h-screen bg-[#0A0A0B] text-white selection:bg-orange-500/30 font-sans relative overflow-x-hidden">
      <CustomStyles />
      <div className="liquid-bg" />
      
      <div className="relative z-10">
        <header className="px-6 py-6 border-b border-white/5 sticky top-0 bg-[#0A0A0B]/80 backdrop-blur-xl z-50">
          <div className="container mx-auto flex justify-between items-center">
             <span className="font-semibold tracking-[0.2em] text-white/30 text-xs uppercase">Social SEO Inc.</span>
             <div className="flex items-center gap-4">
               <Link to="/reports/tvs/scooter" className="text-xs uppercase tracking-widest text-orange-400/80 hover:text-orange-300 transition-colors bg-orange-500/10 px-4 py-2 rounded-full border border-orange-500/20">
                 View Live Dashboard
               </Link>
             </div>
          </div>
        </header>

        <main className="container mx-auto px-4 py-16 md:py-24 max-w-4xl">
          
          {/* Header Section */}
          <div className="mb-20">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-orange-500/10 border border-orange-500/20 rounded-full text-xs font-bold text-orange-300 tracking-widest uppercase mb-8 shadow-[0_0_20px_rgba(99,102,241,0.1)]">
               <span className="w-2 h-2 rounded-full bg-orange-400" />
               Executive Summary
            </div>
            <h1 className="text-5xl md:text-7xl font-black tracking-tighter leading-[1.1] mb-8">
              TVS Scooter Launch <br/>
              <span className="bg-gradient-to-r from-orange-300 via-white to-white bg-clip-text text-transparent">Campaign Analysis</span>
            </h1>
            <p className="text-xl text-white/50 max-w-2xl font-medium leading-relaxed">
              A comprehensive performance review of the TVS Instagram promotion strategy, spanning marketing and entertainment sectors to capture dual demographic targets.
            </p>
          </div>

          <div className="space-y-16">
            
            {/* 1. Campaign Overview */}
            <section>
              <SectionHeading number="01" title="Campaign Overview" />
              <Card>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  <div>
                    <h4 className="text-sm uppercase tracking-widest text-orange-400 font-bold mb-2">Objective</h4>
                    <p className="text-white/80">Support the high-profile launch of the new TVS scooter across 3 targeted rollout phases by generating massive awareness, leveraging viral formats, and fostering active digital engagement.</p>
                  </div>
                  <div>
                    <h4 className="text-sm uppercase tracking-widest text-orange-400 font-bold mb-2">Primary Platforms</h4>
                    <p className="text-white/80">Exclusively executed on <strong>Instagram</strong> to capitalize on the high-reach potential of TikTok-style Reels and highly shareable static content.</p>
                  </div>
                  <div>
                    <h4 className="text-sm uppercase tracking-widest text-orange-400 font-bold mb-2">Campaign Type</h4>
                    <p className="text-white/80">A hybrid content-amplification strategy blending <strong>influencer meme marketing</strong> with structured brand promotion.</p>
                  </div>
                  <div>
                    <h4 className="text-sm uppercase tracking-widest text-orange-400 font-bold mb-2">Page Categories</h4>
                    <ul className="list-disc list-inside text-white/80 space-y-1">
                      <li><strong>Marketing Pages:</strong> Startup, B2B, and Finance.</li>
                      <li><strong>Lifestyle Pages:</strong> Bollywood, Entertainment, and Meme culture.</li>
                    </ul>
                  </div>
                </div>
              </Card>
            </section>

            {/* 2. Strategy & Execution */}
            <section>
              <SectionHeading number="02" title="Strategy & Execution" />
              <Card>
                <h4 className="text-lg text-white font-semibold mb-3">Content Approach & Positioning</h4>
                <p className="mb-6">
                  The content was positioned to seamlessly blend into the native feeds of the respective audiences. Instead of traditional interruptive advertising, the campaign utilized "native-feel" memes on entertainment pages and insightful "startup-story" analyses on business pages.
                </p>
                
                <h4 className="text-lg text-white font-semibold mb-3">Targeting Rationale</h4>
                <div className="bg-white/5 border border-white/5 rounded-xl p-5 mb-6">
                  <p className="mb-3">By segmenting into two distinct categories, we captured a broader yet highly targeted slice of the digitally active urban demographic:</p>
                  <ul className="space-y-3">
                    <li className="flex gap-3">
                      <span className="text-orange-200">▹</span>
                      <span><strong>Marketing/Startup Pages:</strong> Target young professionals, aspiring entrepreneurs, and white-collar workers who value technology, mobility, and modern branding.</span>
                    </li>
                    <li className="flex gap-3">
                      <span className="text-orange-400">▹</span>
                      <span><strong>Bollywood/Meme Pages:</strong> Target Gen-Z and millennial audiences looking for entertainment, pushing the scooter into pop-culture conversations through relatable humor and lifestyle hooks.</span>
                    </li>
                  </ul>
                </div>
              </Card>
            </section>

            {/* 3. Performance Metrics */}
            <section>
              <SectionHeading number="03" title="Performance & Analysis" />
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                <div className="bg-orange-500/10 border border-orange-500/20 rounded-2xl p-6 text-center">
                  <div className="text-3xl font-black text-white mb-1">1.58M+</div>
                  <div className="text-xs uppercase tracking-wider text-orange-300/80">Total Views</div>
                </div>
                <div className="bg-white/10 border border-white/20 rounded-2xl p-6 text-center">
                  <div className="text-3xl font-black text-white mb-1">~9.2k+</div>
                  <div className="text-xs uppercase tracking-wider text-white/80">Total Likes</div>
                </div>
                <div className="bg-white/5 border border-white/10 rounded-2xl p-6 text-center">
                  <div className="text-3xl font-black text-white mb-1">46</div>
                  <div className="text-xs uppercase tracking-wider text-white/50">Total Placements</div>
                </div>
                <div className="bg-white/5 border border-white/10 rounded-2xl p-6 text-center">
                  <div className="text-3xl font-black text-white mb-1">~93%</div>
                  <div className="text-xs uppercase tracking-wider text-white/50">Filmy Dominance</div>
                </div>
              </div>
              <Card>
                <p>
                  Rolled out strategically across 3 distinct phases, the 46 active page placements (12 Marketing, 34 Filmy) generated a highly successful combined reach of <strong>over 1.58 million total views</strong>. 
                </p>
                <p className="mt-4">
                  Spanning Phase 1, Phase 2, and Phase 3, the vast majority of the visibility footprint was anchored by breakout viral video content on the Bollywood network, indicating strong algorithmic favorability for the native entertainment formats.
                </p>
              </Card>
            </section>

            {/* 4. Key Insights */}
            <section>
              <SectionHeading number="04" title="Key Insights & Learnings" />
              <Card>
                <ul className="space-y-6">
                  <li>
                    <h4 className="text-white font-bold mb-1">Performance Split (Bollywood vs Marketing)</h4>
                    <p>The "Filmy" category accounted for roughly 93% of the total viewership, heavily driven by strong engagement on meme pages like <em>filmydhun</em> and <em>filmyactive</em> (500k+ views each). Conversely, the 12 Marketing pages provided a highly consistent, stable baseline of mid-tier viewership (averaging 5k - 20k views per post) ensuring qualitative B2B/Startup demographic penetration.</p>
                  </li>
                  <li>
                    <h4 className="text-white font-bold mb-1">Content Format Favorability</h4>
                    <p>Dynamic Instagram Reels vastly outperformed static image posts in terms of absolute reach. The algorithm continues to heavily bias short-form vertical video for pushing content to non-followers.</p>
                  </li>
                  <li>
                    <h4 className="text-white font-bold mb-1">Audience Engagement Behavior</h4>
                    <p>Entertainment audiences were rapid consumers of the meme content, resulting in sharp spikes of views. Startup audiences displayed slower, more methodical consumption, often resulting in higher qualitative retention (though lower absolute viral potential).</p>
                  </li>
                </ul>
              </Card>
            </section>

            {/* 5. Conclusion */}
            <section>
              <SectionHeading number="05" title="Conclusion" />
              <Card className="bg-gradient-to-br from-white/[0.05] to-transparent border-t-2 border-t-orange-500">
                <p className="mb-4">
                  The TVS Scooter launch campaign successfully met its macro-objective of dominating short-term social awareness. By deploying a diversified network strategy, the brand avoided "single-demographic fatigue." 
                </p>
                <p className="mb-4">
                  <strong>Impact on Brand Visibility:</strong> The 1.58M+ impressions represent significant mindshare capture during a critical launch window. The scooter was positioned not just as a utilitarian vehicle, but as a culturally relevant transport solution via the Bollywood pages, and a smart, modern asset via the Startup platforms.
                </p>
                <p>
                  <strong>Future Recommendations:</strong> For future rollout phases, budget allocation can be leaned slightly toward lifestyle/movie networks to chase massive top-of-funnel viral reach, while reserving roughly 30-40% of the network spend for high-value B2B/startup pages to maintain the vehicle's premium tech-forward positioning.
                </p>
              </Card>
            </section>

          </div>
        </main>

        <footer className="text-center py-16 text-white/30 text-xs mt-12 bg-black/20">
            <p className="tracking-widest uppercase">Social SEO Inc. • Campaign Strategy Report</p>
        </footer>
      </div>
    </div>
  );
}
